import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { useEffect, useState } from 'react'
import { FiBell } from 'react-icons/fi'
import { cn } from '~/@/lib/utils'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useNotifications } from '~/common/hooks/useNotifications'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { NotificationPopoverContent } from '../navigation/NotificationPopoverContent'

type LeftNavNotificationButtonProps = {
  isMobile?: boolean
}

export const LeftNavNotificationButton = ({ isMobile = false }: LeftNavNotificationButtonProps) => {
  const { totalUnreadCount, userHasUnreadNotifications } = useNotifications()
  const { subscribe, unsubscribe } = useDossSignals()
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTypedTranslation()

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
    placement: isMobile ? 'bottom-start' : 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(isMobile ? 0 : 8), flip(), shift({ padding: 0 })],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  useEffect(() => {
    const subscription = subscribe(DossSignalType.CLOSE_NOTIFICATIONS_PANEL, async () => {
      setIsOpen(false)
    })
    return () => {
      unsubscribe(subscription)
    }
  }, [subscribe, unsubscribe])

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex cursor-pointer items-center justify-between rounded px-3 py-2 text-gray-10 hover:bg-gray-2"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex size-5 shrink-0 items-center justify-center">
            <FiBell size={18} />
          </div>
          <span className="truncate text-sm">{t('left_nav.notifications')}</span>
        </div>
        {userHasUnreadNotifications && (
          <div className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded bg-blue-9 px-1.5 text-xs font-medium text-white">
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </div>
        )}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={cn(
              'shadow-large border-gray-4 z-[1301] flex h-[400px] flex-col overflow-hidden rounded border bg-white',
              isMobile ? 'w-screen' : 'w-[450px]',
            )}
            style={floatingStyles}
          >
            <NotificationPopoverContent onClose={() => setIsOpen(false)} />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
